/**
 * A VÁGÓ NÉZET (#89) — a teljes fotó, rajta négy húzható sarok.
 *
 * A mérce: `docs/tervezes/design/kliens-uj-kinezet/kamera-vagas.html`, A
 * változat (Viktor, 2026-11-02): minden fotó után ez jön, és az áttekintőről
 * („Vágás") ugyanez nyílik újra.
 *
 * ═══ MIÉRT KÉZI ═══
 *
 * Az élő lapfelismerő a próbán nem találta a lap szélét (fehér számla világos
 * asztalon), előtte pedig a tételtáblát vágta ki (#88). A felismerő itt csak
 * JAVASLATOT ad a sarkok kezdőhelyére; a döntés az ügyfélé, aki látja, mit
 * vág le.
 *
 * ═══ A KOORDINÁTÁK A FOTÓÉI ═══
 *
 * Az SVG `viewBox`-a a fotó képpont-mérete: a sarkok a fotó koordinátáiban
 * élnek, a kijelzőre a böngésző méretez. A húzás egyetlen helyen fordít
 * kijelző → fotó irányba (`toImagePoint`).
 *
 * ═══ AZ UJJ TAKARJA A SARKOT ═══
 *
 * Húzás közben nagyító jelenik meg a sarok FÖLÖTT (ha nincs hely, alatta):
 * kétszeres nagyítás, célkereszttel. Enélkül a pontos illesztés találgatás.
 *
 * ═══ BILLENTYŰZETTEL IS ═══
 *
 * A sarkok fókuszálhatók, a nyilak 1%-kal, Shifttel 5%-kal mozgatják őket.
 */

import {
  type KeyboardEvent,
  type PointerEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CORNER_NAMES, type CornerName, fullFrameCorners, moveCorner } from './crop-geometry';
import type { QuadCorners } from './document-scanner';

interface CropViewProps {
  /** Az eredeti fotó (`URL.createObjectURL`). */
  src: string;
  /** A fotó képpont-mérete. */
  width: number;
  height: number;
  /** A kezdő sarkok — a javaslat, vagy az előző vágás. */
  initial: QuadCorners;
  /** „2. oldal vágása". */
  title: string;
  /** Az elutasító gomb: friss fotónál „Újrafotózom", újravágásnál „Mégsem". */
  backLabel: string;
  onBack: () => void;
  onDone: (corners: QuadCorners) => void;
  /** Amíg a kivágás fut, a „Kész" nem nyomható újra. */
  busy?: boolean;
}

/** A sarok-fogantyú sugara KIJELZŐ-képpontban; a fotó-koordináta ebből számolódik. */
const HANDLE_RADIUS_PX = 12;
/** Az érintési cél sugara — a 44 px-es padló fele. */
const HIT_RADIUS_PX = 22;
const LOUPE_RADIUS_PX = 52;
const LOUPE_ZOOM = 2;

const KEY_STEP = 0.01;
const KEY_STEP_LARGE = 0.05;

export function CropView({
  src,
  width,
  height,
  initial,
  title,
  backLabel,
  onBack,
  onDone,
  busy = false,
}: CropViewProps) {
  const { t } = useTranslation();
  const [corners, setCorners] = useState<QuadCorners>(initial);
  const [active, setActive] = useState<CornerName | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  // A FOGANTYÚ MÉRETE A KIJELZŐHÖZ IGAZODIK — ehhez a nézetnek egyszer meg
  // kell mérnie magát, és újrarajzolnia (az első rajzoláskor még nincs mit
  // mérni), majd minden átméretezéskor (elforgatott telefon).
  const [, setMeasured] = useState(0);
  useLayoutEffect(() => {
    setMeasured((count) => count + 1);
    const node = svg.current;
    if (!(node && typeof ResizeObserver === 'function')) {
      return;
    }
    const observer = new ResizeObserver(() => setMeasured((count) => count + 1));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const clipId = `nagyito-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;

  /**
   * HÁNY FOTÓ-KÉPPONT EGY KIJELZŐ-KÉPPONT. A `meet` méretezésnél a kisebbik
   * arány dönt. Mérés nélkül (első rajzolás, tesztkörnyezet) egy 360 px-es
   * kijelzővel számolunk — a fogantyú ettől csak a méretében tér el.
   */
  const imagePxPerScreenPx = (): number => {
    const rect = svg.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) {
      return width / 360;
    }
    return 1 / Math.min(rect.width / width, rect.height / height);
  };

  const toImagePoint = (clientX: number, clientY: number) => {
    const rect = svg.current?.getBoundingClientRect();
    // KOORDINÁTA NÉLKÜLI ESEMÉNY (régi böngésző, szintetikus esemény) NEM
    // mozdíthatja a sarkot: egy NaN a sarkot eltüntetné, a vágást elrontaná.
    if (!rect || rect.width === 0 || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
      return null;
    }
    const scale = Math.min(rect.width / width, rect.height / height);
    const offsetX = (rect.width - width * scale) / 2;
    const offsetY = (rect.height - height * scale) / 2;
    return {
      x: (clientX - rect.left - offsetX) / scale,
      y: (clientY - rect.top - offsetY) / scale,
    };
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (!active) {
      return;
    }
    const point = toImagePoint(event.clientX, event.clientY);
    if (point) {
      setCorners((current) => moveCorner(current, active, point, width, height));
    }
  };

  const onKey = (name: CornerName) => (event: KeyboardEvent<SVGGElement>) => {
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step * width, 0],
      ArrowRight: [step * width, 0],
      ArrowUp: [0, -step * height],
      ArrowDown: [0, step * height],
    };
    const move = delta[event.key];
    if (!move) {
      return;
    }
    event.preventDefault();
    setCorners((current) =>
      moveCorner(
        current,
        name,
        { x: current[name].x + move[0], y: current[name].y + move[1] },
        width,
        height
      )
    );
  };

  const k = imagePxPerScreenPx();
  const ring = CORNER_NAMES.map((name) => `${corners[name].x},${corners[name].y}`).join(' ');
  const dim = `M0 0H${width}V${height}H0Z M${CORNER_NAMES.map(
    (name) => `${corners[name].x} ${corners[name].y}`
  ).join(' L')}Z`;

  return (
    <div className="crop" role="dialog" aria-modal="true" aria-label={title}>
      <header className="crop-head">
        <h2>{title}</h2>
        <p>{t('capture.crop.hint')}</p>
      </header>

      <div className="crop-stage">
        <svg
          ref={svg}
          className="crop-svg"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerMove={onPointerMove}
          onPointerUp={() => setActive(null)}
          onPointerCancel={() => setActive(null)}
        >
          <title>{title}</title>
          <image href={src} width={width} height={height} />
          <path d={dim} className="crop-dim" fillRule="evenodd" />
          <polygon points={ring} className="crop-ring" vectorEffect="non-scaling-stroke" />

          {CORNER_NAMES.map((name) => (
            // biome-ignore lint/a11y/useSemanticElements: SVG-ben nincs <button>; a szerep és a billentyű kézzel adott
            <g
              key={name}
              role="button"
              tabIndex={0}
              aria-label={t(`capture.crop.corner.${name}`)}
              className={`crop-handle${active === name ? ' is-active' : ''}`}
              onPointerDown={(event) => {
                event.preventDefault();
                svg.current?.setPointerCapture?.(event.pointerId);
                setActive(name);
              }}
              onKeyDown={onKey(name)}
            >
              <circle
                cx={corners[name].x}
                cy={corners[name].y}
                r={HIT_RADIUS_PX * k}
                className="crop-hit"
              />
              <circle
                cx={corners[name].x}
                cy={corners[name].y}
                r={HANDLE_RADIUS_PX * k}
                className="crop-knob"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ))}

          {active ? (
            <Loupe
              corner={corners[active]}
              k={k}
              src={src}
              width={width}
              height={height}
              ring={ring}
              clipId={clipId}
            />
          ) : null}
        </svg>
      </div>

      <footer className="crop-foot">
        <button type="button" className="pick pick-link" onClick={onBack}>
          {backLabel}
        </button>
        <button
          type="button"
          className="crop-full"
          onClick={() => setCorners(fullFrameCorners(width, height))}
        >
          {t('capture.crop.fullFrame')}
        </button>
        <button
          type="button"
          className="pick pick-primary"
          disabled={busy}
          onClick={() => onDone(corners)}
        >
          {t('capture.crop.done')}
        </button>
      </footer>
    </div>
  );
}

/** A nagyító — a sarok környéke kétszeresen, a sarok FÖLÖTT (ha nincs hely, alatta). */
function Loupe({
  corner,
  k,
  src,
  width,
  height,
  ring,
  clipId,
}: {
  corner: { x: number; y: number };
  k: number;
  src: string;
  width: number;
  height: number;
  ring: string;
  clipId: string;
}) {
  const radius = LOUPE_RADIUS_PX * k;
  const gap = radius * 1.7;
  const cx = Math.min(Math.max(corner.x, radius), width - radius);
  const cy = corner.y - gap - radius > 0 ? corner.y - gap : corner.y + gap;

  return (
    <g className="crop-loupe">
      <clipPath id={clipId}>
        <circle cx={cx} cy={cy} r={radius} />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <circle cx={cx} cy={cy} r={radius} className="crop-loupe-bg" />
        <g
          transform={`translate(${cx} ${cy}) scale(${LOUPE_ZOOM}) translate(${-corner.x} ${-corner.y})`}
        >
          <image href={src} width={width} height={height} />
          <polygon points={ring} className="crop-ring" vectorEffect="non-scaling-stroke" />
        </g>
      </g>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        className="crop-loupe-rim"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={cx - 10 * k}
        y1={cy}
        x2={cx + 10 * k}
        y2={cy}
        className="crop-cross"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={cx}
        y1={cy - 10 * k}
        x2={cx}
        y2={cy + 10 * k}
        className="crop-cross"
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}
